import type { AuthProvider, UserIdentity } from 'react-admin';

import type { AdminRole } from '@/admin/adminApi';
import { isForbidden } from '@/admin/users/adminErrors';
import { messageForErrorCode } from '@/api/apiError';
import type { HttpClient } from '@/api/httpClient';
import { SessionExpiredError } from '@/api/httpClient';
import type { StoredSession } from '@/api/tokenStorage';

import { setLoginNotice } from './loginNotice';

/**
 * `authProvider` React Admin sur l'authentification par jetons opaques.
 *
 * Le backend n'emet **pas** de JWT : rien n'est lisible cote client, et le
 * firewall Symfony ne fait pas l'authentification — c'est
 * `CurrentIdentityUserProvider` qui lit `Authorization: Bearer`, hache le jeton
 * et verifie qu'une session est active. Les consequences pour ce provider :
 *
 * - **Aucun role ne se deduit d'un jeton.** `getPermissions` et `getIdentity`
 *   rendent le role d'administration **lu sur `GET /admin/me`** (ADR 0032,
 *   point 7), une fois par session et en memoire : c'est la seule source. Ce
 *   role sert au confort de l'interface (le nommer, masquer une entree) ; le
 *   serveur reste l'autorite et refuse en 403 ce qu'il refuse.
 * - **`checkAuth` ne fait aucun appel reseau.** Il lit la session locale. Un
 *   backoffice qui interroge `/auth/me` a chaque changement d'ecran deconnecte
 *   l'utilisateur des que le reseau hoquette ; le vrai controle est le 401 des
 *   requetes de donnees, que `httpClient` recouvre par un rafraichissement.
 * - **Un compte sans role d'administration n'ouvre pas de session ici.** `login`
 *   lit le role (la meme lecture unique que `getIdentity` et `getPermissions`,
 *   mise en memoire pour eux) et refuse le compte parent a la porte — avec une
 *   explication — au lieu de lui montrer des ecrans qui repondent 403. Le
 *   controle d'acces reste celui du serveur, qui garde chaque route `/admin/*`.
 */

/** Compte authentifie mais sans role `support` ni `admin` : refuse a la connexion. */
export class NotAnAdministratorError extends Error {
  constructor() {
    super("Ce compte n'a pas d'accès à l'administration. Demandez un rôle à un administrateur.");
    this.name = 'NotAnAdministratorError';
  }
}

/** Corps de `/auth/login`. */
interface LoginParams {
  readonly username?: unknown;
  readonly password?: unknown;
}

const isStoredSessionResponse = (value: unknown): value is StoredSession =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Record<string, unknown>).tokens === 'object' &&
  typeof (value as Record<string, unknown>).user === 'object';

/**
 * Identite de la barre superieure : celle de la session, plus le role.
 *
 * `adminRole` vaut `undefined` quand le role n'a pas pu etre lu (API injoignable,
 * 500) : l'interface dit alors « Rôle indisponible » plutot que d'en supposer un.
 * `null` est une reponse du serveur : aucun role d'administration. Le cas courant
 * est un 200 avec `adminRole: null` (tout compte parent, `AdminMeController`) ;
 * un 403, s'il arrive, est lu de la meme facon.
 */
export interface BackofficeIdentity extends UserIdentity {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly adminRole?: AdminRole | null;
}

/** Lit le role dans la reponse de `/admin/me` sans supposer sa forme. */
const roleIn = (body: unknown): AdminRole | null => {
  const role: unknown =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>).adminRole : null;

  return role === 'support' || role === 'admin' ? role : null;
};

export const createAuthProvider = (http: HttpClient): AuthProvider => {
  /**
   * Lecture unique de `/admin/me` par session, partagee par `getIdentity` et
   * `getPermissions`. La cle est l'utilisateur de la session : un autre compte
   * connecte dans le meme onglet relit, et la connexion comme la deconnexion
   * vident la memoire (un role change en console se voit a la reconnexion). Un
   * role `null` (200, ou 403) est une reponse et reste en memoire ; une panne ne l'est
   * pas, la demande suivante retente.
   */
  let roleCache: { readonly userId: string; readonly role: Promise<AdminRole | null> } | null = null;

  const readRole = (userId: string): Promise<AdminRole | null> => {
    if (roleCache?.userId === userId) {
      return roleCache.role;
    }

    const entry: { readonly userId: string; role: Promise<AdminRole | null> } = {
      userId,
      role: Promise.resolve(null),
    };

    // En memoire AVANT l'appel : un echec synchrone doit pouvoir retirer
    // l'entree, ce qu'il ne pourrait pas si elle etait posee apres.
    roleCache = entry;

    // Fonction `async` et non `.then()` : un client qui echouerait de facon
    // synchrone serait rattrape aussi (meme raison que `useAdminAccess`).
    entry.role = (async (): Promise<AdminRole | null> => {
      try {
        return roleIn(await http.request<unknown>({ path: '/admin/me' }));
      } catch (error: unknown) {
        if (isForbidden(error)) {
          return null;
        }

        if (roleCache === entry) {
          roleCache = null;
        }

        throw error;
      }
    })();

    return entry.role;
  };

  /**
   * Revoque la session cote serveur, puis l'oublie localement.
   *
   * Revoquer, pas seulement oublier : un jeton oublie reste valide jusqu'a son
   * expiration. Une revocation impossible (API arretee, jeton deja expire) ne
   * doit pas empecher la sortie locale : la personne a demande a sortir, ou n'a
   * pas a rester connectee.
   */
  const endSession = async (): Promise<void> => {
    roleCache = null;

    if (http.session() !== null) {
      try {
        await http.request({ method: 'POST', path: '/auth/logout' });
      } catch {
        // Voir ci-dessus.
      }
    }

    http.clearSession();
  };

  return {
    login: async (params: LoginParams) => {
      roleCache = null;

      const email = typeof params.username === 'string' ? params.username.trim() : '';
      const password = typeof params.password === 'string' ? params.password : '';

      if (email === '' || password === '') {
        throw new Error(messageForErrorCode('validation_failed'));
      }

      const session = await http.request<unknown>({
        method: 'POST',
        path: '/auth/login',
        body: { email, password },
        // Aucun jeton a poser, et surtout aucun rejeu : un mot de passe errone
        // doit remonter tel quel, pas declencher un rafraichissement.
        anonymous: true,
      });

      if (!isStoredSessionResponse(session)) {
        throw new Error("La réponse de connexion de l'API est inexploitable.");
      }

      http.setSession(session);

      // Le role se lit sur le serveur, jamais dans le jeton (opaque). Tant qu'il
      // n'est pas connu, la session n'est pas gardee : un echec de cette lecture
      // (reseau, 401) ferme la session fraiche au lieu de la laisser ouverte sur
      // un role inconnu. La lecture reste en memoire pour la barre superieure.
      try {
        if ((await readRole(session.user.id)) === null) {
          throw new NotAnAdministratorError();
        }
      } catch (cause) {
        await endSession();

        throw cause;
      }
    },

    logout: endSession,

    checkAuth: () => (http.session() === null ? Promise.reject(new Error('Non authentifié.')) : Promise.resolve()),

    /**
     * Appele par React Admin quand une requete de donnees echoue.
     *
     * Ne deconnecte que sur un 401 **non recouvre** : `httpClient` a deja tente un
     * rafraichissement et un rejeu avant d'arriver ici. Un 403 ne deconnecte
     * jamais — l'utilisateur est bien authentifie, il n'a simplement pas les droits
     * sur ce profil bebe, et l'ejecter vers l'ecran de connexion serait un
     * diagnostic faux.
     */
    checkError: (error: unknown) => {
      if (error instanceof SessionExpiredError) {
        http.clearSession();
        setLoginNotice('session_expired');

        return Promise.reject(new Error(error.message));
      }

      const status =
        typeof error === 'object' && error !== null
          ? (error as { status?: unknown }).status
          : undefined;

      if (status === 401) {
        http.clearSession();
        setLoginNotice('session_expired');

        return Promise.reject(new Error('Session expirée. Reconnectez-vous.'));
      }

      return Promise.resolve();
    },

    getIdentity: async (): Promise<BackofficeIdentity> => {
      const session = http.session();

      if (session === null) {
        throw new Error('Non authentifié.');
      }

      // Le nom quand il existe, l'adresse sinon. Le repli n'est pas un pis-aller :
      // l'etat civil est facultatif cote produit (ADR 0020 rend obligatoire le
      // compte, pas le nom), et un exploitant qui ne s'est pas nomme doit quand
      // meme se reconnaitre dans la barre superieure.
      const displayName = session.user.displayName ?? null;
      const identity = {
        id: session.user.id,
        fullName: displayName === null || displayName === '' ? session.user.email : displayName,
        email: session.user.email,
      };

      try {
        return { ...identity, adminRole: await readRole(session.user.id) };
      } catch {
        // Le nom reste affichable sans le role : une panne de `/admin/me` ne doit
        // pas vider la barre superieure.
        return identity;
      }
    },

    /**
     * Le role lu sur `/admin/me` : `'support'`, `'admin'`, ou `null` (compte sans
     * role : 200 avec `adminRole: null`, ou 403). Une
     * panne rejette, et React Admin passe l'erreur a `checkError`, qui ne
     * deconnecte que sur un 401.
     */
    getPermissions: async (): Promise<AdminRole | null> => {
      const session = http.session();

      if (session === null) {
        throw new Error('Non authentifié.');
      }

      return readRole(session.user.id);
    },
  };
};
